/** @format */
import { ChangeDetectionStrategy, Component, inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { StateService } from '@uirouter/core';
import { SuomiFiUserBasicDetails } from 'common-typescript/types';
import { BehaviorSubject, catchError, EMPTY, Observable } from 'rxjs';
import { AlertsService, AlertType, StudyVoucherPaymentService } from 'sis-components';
import { OpenUniversityErrorHelperService } from '../../service/open-university-error-helper.service';
import { SuomiFiUserDetailsFormService } from '../../service/suomi-fi-user-details-form.service';
import { CART_PURCHASE_WIZARD_STEP_KEYS, STEP } from '../constants';

@Component({
    selector: 'app-study-voucher-studies-activate',
    standalone: false,
    templateUrl: './study-voucher-studies-activate.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StudyVoucherStudiesActivateComponent implements OnInit {
    private readonly alertsService = inject(AlertsService);
    private readonly stateService = inject(StateService);
    private readonly studyVoucherPaymentService = inject(StudyVoucherPaymentService);
    private readonly suomiFiUserDetailsFormService = inject(SuomiFiUserDetailsFormService);
    private readonly translocoService = inject(TranslocoService);
    private readonly openUniversityErrorHelper = inject(OpenUniversityErrorHelperService);

    @Input() transactionId: string;
    @Input() suomiFiUserBasicDetails: SuomiFiUserBasicDetails;

    form$: Observable<FormGroup>;
    readonly activationInProgress$ = new BehaviorSubject<boolean>(false);
    readonly wizardSteps: readonly string[] = CART_PURCHASE_WIZARD_STEP_KEYS;
    protected readonly currentStep = STEP.ACTIVATE_STUDIES;

    ngOnInit(): void {
        this.form$ = this.suomiFiUserDetailsFormService.buildForm(this.suomiFiUserBasicDetails);
    }

    activate(form: FormGroup): void {
        if (this.activationInProgress$.value) {
            return;
        }
        if (!this.checkFormValidity(form)) {
            return;
        }
        this.activationInProgress$.next(true);
        const openUniversityUserBasicDetails = this.suomiFiUserDetailsFormService.getOpenUniversityUserBasicDetails(
            form,
            this.suomiFiUserBasicDetails,
        );
        this.studyVoucherPaymentService
            .activate(this.transactionId, openUniversityUserBasicDetails)
            .pipe(
                catchError(() => {
                    this.openUniversityErrorHelper.showEnrolmentConfirmationFailureAlert();
                    return EMPTY;
                }),
            )
            .subscribe(activatedCart => {
                this.alertsService.addAlert({
                    message: this.translocoService.translate('OPEN_UNIVERSITY.STUDY_VOUCHER_STUDIES_ACTIVATE.SUCCESS_ALERT'),
                    type: AlertType.SUCCESS,
                });
                this.stateService.go(
                    'student.open-university-cart.study-voucher-confirmation-completed',
                    { cartParam: activatedCart },
                    { custom: { skipConfirmationDialog: true } },
                );
            })
            .add(() => this.activationInProgress$.next(false));
    }

    private checkFormValidity(form: FormGroup): boolean {
        const identifier = 'study-voucher-activate-validation-err';
        if (form.valid) {
            this.alertsService.dismissAlertIfExists(identifier);
            return true;
        }
        form.markAllAsTouched();
        this.alertsService.addAlert({
            message: this.translocoService.translate('OPEN_UNIVERSITY.VALIDATION_ERROR_ALERT'),
            type: AlertType.DANGER,
            identifier,
        });
        return false;
    }
}
